import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ModalDialogComponent } from '@vg-constellation/angular-17/modal-dialog';
import { FundInvestiment, ResponseGetAllWatchlist, Watchlist, WatchlistsItemCategories } from './types/add-wl.types';
import { WatchlistService } from './service/watchlist.service';
import { tokenInterceptorProvider } from './providers/token-interceptor-config';
import { GlobalSettingsService } from './service/global-settings.service';
import { SettingsService } from './service/settings.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'add-to-wl',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [WatchlistService, tokenInterceptorProvider],
})
export class AddToWatchlistComponent implements OnInit, OnChanges {
  @ViewChild('modalCheckbox') modalCheckbox: ModalDialogComponent;
  @ViewChild('modalEmptyWatchlist') modalEmptyWatchlist: ModalDialogComponent;
  @ViewChild('modalSuccess') modalSuccess: ModalDialogComponent;
  @ViewChild('modalCreateWl') modalCreateWl: ModalDialogComponent;
  @ViewChild('generalError') generalError: ModalDialogComponent;

  @Input() fundData: string;
  fundInvestiment: FundInvestiment;

  title: string = '';
  chipTrue: boolean = false;
  canOpenModal: boolean = false;
  watchlists: Watchlist[] = [];
  widsWithFund: string[] = [];
  linkWatchlist: string = ''
  widsWithFundValidate: string[] = []; // It's only used to compare with the original
  newWatchlistName: string = '';
  chipAddWatchlistText: string = '';
  nothingChecked: boolean = false;
  errorOnCreateWatchlist: boolean = false;
  errorOnSaveChanges: boolean = false;
  errorOnCreateWatchlistSave: boolean = false;
  errorOnCreateWatchlistMessage: string = '';
  errorMessageOnAddFundToWatchlist: string = "";
  errorMessageOnNothingChecked: string = '';

  constructor(
    private watchlistService: WatchlistService,
    private settingsService: SettingsService,
    private globalSettingsService: GlobalSettingsService,
    private cookiesService: CookieService
  ) { }

  async ngOnInit(): Promise<void> {
    this.settingsService.initializeConfig(window.location.href);
    await this.globalSettingsService.getStarted();
    this.validateCookie();
    this.linkWatchlist = this.settingsService.getWatchlistUrl();

    if (this.fundData) {
      this.parseAndSetFundData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fundData && !changes.fundData.isFirstChange()) {
      this.parseAndSetFundData();
    }
  }

  parseAndSetFundData(): void {
    if (this.isFundInvestment(JSON.parse(this.fundData))) {
      try {
        this.fundInvestiment = JSON.parse(this.fundData);
        this.getWatchlistsAndDecideTitle();
      } catch (error) {
        throw new Error(`Error on object generation: ${error}`);
      }
    } else {
      this.generalError.openModalDialog();
      throw new Error('Object Invalid');
    }
  }

  openCheckbox() {
    if (this.isAllowedToOpenModal()) {
      if (this.watchlists.length > 0) {
        this.modalCheckbox.openModalDialog();
      } else {
        this.modalEmptyWatchlist.openModalDialog();
      }
    } else {
      this.globalSettingsService.sendToLogin();
    }
  }

  async getWatchlistsAndDecideTitle() {
    this.watchlistService.getAllWatchlists(this.fundInvestiment.label).subscribe((data) => {
        this.defineWatchlists(data);
        this.title =
          this.watchlists.length > 0
            ? `Add ${this.fundInvestiment.label} (${this.fundInvestiment.ticker}) to a watchlist:`
            : `You don't have any watchlists. To add ${this.fundInvestiment.label} (${this.fundInvestiment.ticker}), create a watchlist.`;
    });
  }

  defineWatchlists(data: ResponseGetAllWatchlist) {
    this.watchlists = data.watchlists;
    this.widsWithFund = data.watchlistsWithFund;
    this.widsWithFundValidate = [...data.watchlistsWithFund];
  }

  getAllWatchlists() {
    this.watchlistService.getAllWatchlists(this.fundInvestiment.label).subscribe((data) => {
      this.defineWatchlists(data);
    });
  }

  saveWatchlistChanges(): void {
    this.fundInvestiment.details = {};
    this.fundInvestiment.settings = {};
    this.fundInvestiment.poid = this.settingsService.getPoid();

    if (this.widsWithFund.length === 0) {
      this.nothingChecked = true;
      this.errorMessageOnNothingChecked = 'A selection is required to save your watchlist changes.';
      return;
    }

    this.watchlists.forEach((watchlist) => {
      if (this.modalCheckbox) {
        this.modalCheckbox.closeDialogModal();
      }
      this.watchlistService.updateWatchlist(watchlist, watchlist.wid).subscribe({
        next: (value) => {
          console.log('Watchlist updated', value);
          this.errorOnSaveChanges = false;
          this.modalSuccess.openModalDialog();
        },
        error: (error) => {
          console.error('Error updating watchlist', error);
          this.errorOnSaveChanges = true;
        },
      });
    });

    this.clearMessages();
  }

  createWatchlist(): void {
    if (this.validateWatchlisCreation()) {
      const watchlist = { title: this.newWatchlistName, settings: {} };
      this.watchlistService.createNewWatchlist(watchlist).subscribe({
        next: (value) => {
          this.getWatchlistsAndDecideTitle();
          this.widsWithFund.push(value.wid);
          this.chipAddWatchlistText = `New watchlist "${this.newWatchlistName}" was successfully created.`;
          this.chipTrue = true;
          this.modalCheckbox.openModalDialog();
          this.errorOnCreateWatchlistSave = false;
          this.newWatchlistName = ''
        },
        error: (error) => {
          console.error('Error creating watchlist', error);
          this.errorOnCreateWatchlistSave = true;
        },
      });
    }
  }

  cancel(): void {
    if (this.modalCheckbox) {
      this.modalCheckbox.closeDialogModal();
      this.getAllWatchlists();
    }

    if (this.modalCreateWl) {
      this.modalCreateWl.closeDialogModal();
    }

    this.clearMessages();

  }

  clearMessages() {
    this.errorOnCreateWatchlistSave = false;
    this.errorOnSaveChanges = false;
    this.newWatchlistName = ''
    this.chipAddWatchlistText = '';
    this.chipTrue = false;
    this.nothingChecked = false;
    this.errorMessageOnNothingChecked = '';
    this.errorOnCreateWatchlistMessage = '';
    this.errorOnCreateWatchlist = false;

    const descriptiveElements = document.getElementsByClassName('c11n-checkbox__descriptive-label');
    Array.from(descriptiveElements).forEach((el: Element) => {
      (el as HTMLElement).innerHTML = "";
      (el as HTMLElement).style.setProperty('display', 'none', 'important');
    });
  }

  openModalCreateWl() {
    this.modalCreateWl.openModalDialog();
  }

  checkCreateWatchlistsLength(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.newWatchlistName = inputElement.value;
    this.validateWatchlisCreation();
  }

  validateWatchlisCreation(): boolean {
    const nameLength = this.newWatchlistName.length;
    this.getAllWatchlists();

    if (this.watchlists.length >= 6) {
      this.errorOnCreateWatchlist = true;
      this.errorOnCreateWatchlistMessage = 'You already have 6 watchlists registered.';
      return false;
    }

    if (nameLength === 0) {
      this.errorOnCreateWatchlist = true;
      this.errorOnCreateWatchlistMessage = 'This field is required.';
      return false;
    }

    if (nameLength > 50) {
      this.errorOnCreateWatchlist = true;
      this.errorOnCreateWatchlistMessage = `${nameLength}/50 characters (1 character minimum)`;
      return false;
    }

    if (this.watchlists.some((watchlist) => watchlist.title === this.newWatchlistName)) {
      this.errorOnCreateWatchlist = true;
      this.errorOnCreateWatchlistMessage = 'Please use a unique watchlist name.';
      return false;
    }

    this.errorOnCreateWatchlist = false;
    return true;
  }

  addRemoveWatchlist(wid: string): void {
    const watchlist = this.watchlists.find((watchlist) => watchlist.wid == wid);
    const watchlistIndex = this.watchlists.findIndex((watchlist) => watchlist.wid == wid);
    const targetElement = document.getElementById(wid) as HTMLInputElement;
    const descriptiveElement = targetElement.nextElementSibling?.getElementsByClassName("c11n-checkbox__descriptive-label")[0];

    if (!watchlist || !descriptiveElement) return;

    if (targetElement.checked) {
      if (!this.hasFundInvestmentTicker(watchlist)) {
        if (watchlist.watchlistItems.length < 30) {
          watchlist.watchlistItems.push({ ...this.fundInvestiment });
          this.watchlists[watchlistIndex] = watchlist;
          this.widsWithFund.push(wid);
          this.updateDescriptiveElement(descriptiveElement as HTMLElement, "");
        } else {
          this.updateDescriptiveElement(
            descriptiveElement as HTMLElement,
            "Error: You've reached your maximum of 30 investments in this watchlist. To add an investment, select another watchlist or go to watchlist to delete an investment."
          );
          targetElement.checked = false;
        }
      }
    } else {
      if (this.hasFundInvestmentTicker(watchlist)) {
        const index = watchlist.watchlistItems.findIndex(
          (item) => item.ticker === this.fundInvestiment.ticker
        );
        if (index !== -1) {
          watchlist.watchlistItems.splice(index, 1);
        }
        this.widsWithFund = this.widsWithFund.filter((filterWid) => filterWid !== watchlist.wid);
        this.updateDescriptiveElement(descriptiveElement as HTMLElement, "");
      }
    }
  }

  private updateDescriptiveElement(element: HTMLElement, message: string): void {
    element.innerHTML = message;
    if (message && message.length > 0) {
      element.style.setProperty('display', 'block', 'important');
    } else {
      element.style.setProperty('display', 'none', 'important');
    }
  }

  isFundInvestment(obj: any): boolean {
    return (
      typeof obj === 'object' &&
      typeof obj.category === 'string' &&
      typeof obj.cusip === 'string' &&
      typeof obj.label === 'string' &&
      typeof obj.link === 'string' &&
      typeof obj.ticker === 'string' &&
      Object.values(WatchlistsItemCategories).includes(obj.category as WatchlistsItemCategories)
    );
  }

  private hasFundInvestmentTicker(watchlist: Watchlist): boolean {
    return watchlist.watchlistItems.some(item => item.ticker === this.fundInvestiment.ticker);
  }

  isAllowedToOpenModal(): boolean {
    if (this.settingsService.getPoid() && this.fundData) {
      return true;
    }

    this.cookiesService.set('canOpenModal', 'false', 1);
    return false;
  }

  validateCookie() {
    const poid = this.settingsService.getPoid();
    if (this.cookiesService.get('canOpenModal') === 'false' && poid) {
      this.modalCheckbox.openModalDialog();
      this.cookiesService.delete('canOpenModal');
    }
  }

  redirectToWatchlistPage() {
    window.location.href = this.settingsService.getWatchlistUrl();
  }
}
